import React from 'react';

import { ArrowInCircleRightIcon } from '@vlabs/icons';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';
import type { LinkProps as LibLinkProps } from 'react-router-dom';
import { Link as LibLink } from 'react-router-dom';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import './Link.sass';

type LinkSize = 'large' | 'medium' | 'small';
type LinkSurface = 'default' | 'inverted';
type LinkType = 'primary' | 'secondary';

export interface LinkProps extends Omit<LibLinkProps, 'className'>,
  WithDataTestid,
  WithClassNameProp {
  type?: LinkType;
  size?: LinkSize;
  withIcon?: boolean;
  surface?: LinkSurface;
  disabled?: boolean;
}

const displayName = 'Link';
const testid = buildTestid(displayName);

function Link({
  className = undefined,
  type = 'primary',
  size = 'medium',
  withIcon = false,
  surface = 'default',
  'data-testid': dataTestId = undefined,
  children = undefined,
  disabled = false,
  ...props
}: LinkProps): React.JSX.Element {
  if (disabled) {
    return (
      <span
        className={classNames(
          'Link',
          `Link_type-${type}`,
          `Link_size-${size}`,
          `Link_surface-${surface}`,
          { Link_disabled: disabled },
          className,
        )}
        {...testid(dataTestId)}
      >
        <div className="Link__Content">
          {children}
        </div>
        {!!withIcon && <ArrowInCircleRightIcon className="Link__Icon" />}
      </span>
    );
  }

  return (
    <LibLink
      className={classNames(
        'Link',
        `Link_type-${type}`,
        `Link_size-${size}`,
        `Link_surface-${surface}`,
        className,
      )}
      {...testid(dataTestId)}
      {...props}
    >
      <div className="Link__Content">
        {children}
      </div>
      {!!withIcon && <ArrowInCircleRightIcon className="Link__Icon" />}
    </LibLink>
  );
}

Link.displayName = displayName;

export { Link };
